import { FC, useMemo } from 'react';
import { Checkbox, ComboBox } from '@carbon/react';
import {
  ColumnDateFilter,
  FilterSection,
  useDataTableContext,
} from 'components/data-table';
import { useAtom } from 'jotai';
import { debounce } from 'lodash-es';
import type { SalesforceAccountResponse } from 'types/api-responses';
import { createPayorPayeeAccountsAtom } from './atoms';
import { SOURCE_FILTER_OPTIONS } from './columns';
import styles from './royalty-checks-filters.module.scss';

const SourceCheckboxes: FC = () => {
  const { table } = useDataTableContext();
  const column = table.getColumn('source');
  const selected = (column?.getFilterValue() as string[] | undefined) ?? [];

  const toggle = (value: string, next: boolean) => {
    const nextSet = new Set(selected);
    if (next) nextSet.add(value);
    else nextSet.delete(value);
    const nextArray = Array.from(nextSet);
    column?.setFilterValue(nextArray.length > 0 ? nextArray : undefined);
  };

  return (
    <div className={styles.sourceFilter}>
      {SOURCE_FILTER_OPTIONS.map(({ value, label }) => (
        <Checkbox
          key={value}
          id={`rc-source-${value.toLowerCase()}`}
          labelText={label}
          checked={selected.includes(value)}
          onChange={(_e, { checked }) => toggle(value, checked)}
        />
      ))}
    </div>
  );
};

interface AccountTypeaheadProps {
  id: string;
  columnId: string;
  label: string;
  placeholder: string;
}

const AccountTypeahead: FC<AccountTypeaheadProps> = ({
  id,
  columnId,
  label,
  placeholder,
}) => {
  const { table } = useDataTableContext();
  const column = table.getColumn(columnId);

  const { searchAtom } = useMemo(() => createPayorPayeeAccountsAtom(), []);
  const [state, setSearchPayload] = useAtom(searchAtom);
  const results = state?.data?.results ?? [];

  const debouncedSearch = useMemo(
    () => debounce(setSearchPayload, 400),
    [setSearchPayload]
  );

  const filterValue = (column?.getFilterValue() as string | undefined) ?? null;
  const selected = filterValue
    ? (results.find((a) => a.name === filterValue) ??
      ({ name: filterValue } as SalesforceAccountResponse))
    : null;

  return (
    <ComboBox
      size="md"
      id={id}
      titleText={label}
      items={results}
      placeholder={placeholder}
      itemToString={(a) => a?.name ?? ''}
      selectedItem={selected}
      onChange={({ selectedItem }) =>
        column?.setFilterValue(selectedItem?.name || undefined)
      }
      onInputChange={(text) => {
        debouncedSearch((current) => ({
          ...current,
          text,
          pageSize: 20,
        }));
      }}
    />
  );
};

interface Props {
  createdByOptions: string[];
}

const RoyaltyChecksFilters: FC<Props> = ({ createdByOptions }) => {
  const { table } = useDataTableContext();
  const createdByColumn = table.getColumn('createdByName');
  const createdByValue =
    (createdByColumn?.getFilterValue() as string | undefined) ?? null;

  return (
    <div className={styles.filtersWrapper}>
      <FilterSection title="Date Filters">
        <ColumnDateFilter columnId="checkIssueDate" label="Check Issue Date" />
        <ColumnDateFilter columnId="createdAt" label="Created At" />
        <ColumnDateFilter columnId="updatedAt" label="Updated At" />
      </FilterSection>

      <FilterSection title="Source">
        <SourceCheckboxes />
      </FilterSection>

      <FilterSection title="Royalty Check Filters">
        <AccountTypeahead
          id="rc-payor-filter"
          columnId="payorName"
          label="Payor"
          placeholder="Search payors..."
        />
        <AccountTypeahead
          id="rc-payee-filter"
          columnId="payeeName"
          label="Payee"
          placeholder="Search payees..."
        />
        <ComboBox
          size="md"
          id="rc-created-by-filter"
          titleText="Created By"
          items={createdByOptions}
          selectedItem={createdByValue}
          placeholder="Search by name..."
          itemToString={(item) => item ?? ''}
          onChange={({ selectedItem }) =>
            createdByColumn?.setFilterValue(selectedItem ?? undefined)
          }
        />
      </FilterSection>
    </div>
  );
};

export { RoyaltyChecksFilters };
